/**
 * SignalBreakdownBars — Polished, editorial signal breakdown for Discover.
 *
 * REPLACES the previous hand-drawn striped marker bars (which felt amateur
 * next to the rest of the polished card content). This version:
 *
 *   • Clean Inter/Inter Tight typography (no hand-drawn marker fonts).
 *   • Subtle progress bars with a small scarlet fill.
 *   • Hides the "domain" row when domain didn't contribute any score —
 *     a 0-score domain row used to read like a deficit ("DOMAIN: no match")
 *     when in reality it just means the domain is novel and we relied on
 *     content. Now we only surface signal sources that actually fired.
 *   • Hover tooltip reveals the exact matched phrases with monospace styling.
 *   • Total weight bubble shows the cumulative score for transparency.
 */
import React, { useState } from 'react';

const SCARLET = '#CD2200';

function pct(v, max) {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.max(0, (v / max) * 100));
}

function Tooltip({ visible, items, label }) {
  if (!visible) return null;
  if (!items || items.length === 0) {
    return (
      <div
        className="absolute left-0 top-full mt-2 px-3 py-2 rounded-lg pointer-events-none z-20"
        style={{
          background: '#0F172A',
          color: 'rgba(255,255,255,0.9)',
          fontSize: 11,
          minWidth: 180,
          boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
        }}
      >
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] opacity-50 mb-0.5">{label}</div>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', opacity: 0.7 }}>
          (no matches)
        </div>
      </div>
    );
  }
  return (
    <div
      className="absolute left-0 top-full mt-2 px-3 py-2 rounded-lg pointer-events-none z-20"
      style={{
        background: '#0F172A',
        color: 'rgba(255,255,255,0.92)',
        fontSize: 11,
        minWidth: 220,
        maxWidth: 360,
        boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
      }}
    >
      <div className="text-[9px] font-mono uppercase tracking-[0.14em] opacity-50 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 8).map((item, i) => (
          <span
            key={i}
            className="px-1.5 py-0.5 rounded"
            style={{
              background: 'rgba(255,255,255,0.06)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 10.5,
            }}
          >
            {item}
          </span>
        ))}
        {items.length > 8 && (
          <span style={{ opacity: 0.55, fontSize: 10 }}>+{items.length - 8} more</span>
        )}
      </div>
    </div>
  );
}

function SignalRow({ label, value, max, matchedItems, hint }) {
  const [hover, setHover] = useState(false);
  const fillPct = pct(value, max);
  return (
    <div
      className="relative grid grid-cols-[80px_1fr_auto] items-center gap-3 py-1.5 cursor-default"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`signal-row-${label.toLowerCase()}`}
    >
      <div
        className="text-[10px] font-mono uppercase tracking-[0.16em]"
        style={{ color: 'var(--g-muted-2, #6B6B6B)' }}
      >
        {label}
      </div>
      <div className="relative">
        <div
          className="h-[6px] w-full rounded-full overflow-hidden"
          style={{ background: 'rgba(15,23,42,0.06)' }}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${fillPct}%`,
              background: `linear-gradient(90deg, ${SCARLET} 0%, ${SCARLET}cc 100%)`,
              transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        </div>
      </div>
      <div
        className="text-[11px] font-mono tabular-nums w-[36px] text-right"
        style={{ color: 'var(--g-text, #0F172A)' }}
      >
        {value}
      </div>
      <Tooltip visible={hover} items={matchedItems} label={hint || `${label} matches`} />
    </div>
  );
}

export default function SignalBreakdownBars({ breakdown, matches }) {
  if (!breakdown) return null;

  const domain = Number(breakdown.domain) || 0;
  const title = Number(breakdown.title) || 0;
  const keyword = Number(breakdown.keyword) || 0;
  const total = domain + title + keyword;
  if (total === 0) return null;

  // Normalize all bars to the largest score so the visual reads as
  // "share of the verdict" rather than absolute weight.
  const max = Math.max(domain, title, keyword, 1);

  // Only show rows that actually contributed. A 0-score domain row used to
  // appear as "no match" — that read as a deficit. Now: silence is success.
  const rows = [];
  if (domain > 0) {
    rows.push({ label: 'Domain',  value: domain,  matched: matches?.domain || [],  hint: 'Domain hints matched' });
  }
  if (title > 0) {
    rows.push({ label: 'Title',   value: title,   matched: matches?.title || [],   hint: 'Title / description phrases matched' });
  }
  if (keyword > 0) {
    rows.push({ label: 'Keyword', value: keyword, matched: matches?.keyword || [], hint: 'Body keywords + phrases matched' });
  }

  return (
    <div className="rounded-xl px-4 py-3.5" style={{ background: 'rgba(15,23,42,0.025)', border: '1px solid rgba(15,23,42,0.06)' }}>
      <div className="flex items-center justify-between mb-2.5">
        <span
          className="text-[10px] font-mono uppercase tracking-[0.16em]"
          style={{ color: SCARLET }}
        >
          Signal breakdown
        </span>
        <span
          className="text-[10px] font-mono"
          style={{ color: 'var(--g-muted-2, #6B6B6B)' }}
          title="Total weighted score across all signal sources"
        >
          weight {total}
        </span>
      </div>
      <div className="space-y-0.5">
        {rows.map((r) => (
          <SignalRow
            key={r.label}
            label={r.label}
            value={r.value}
            max={max}
            matchedItems={r.matched}
            hint={r.hint}
          />
        ))}
      </div>
      <div
        className="text-[10px] mt-2.5"
        style={{ color: 'var(--g-muted-2, #6B6B6B)' }}
      >
        Hover any row to see the exact matches.
      </div>
    </div>
  );
}
